import { useCallback, useEffect, useRef, useState } from "react";
import { IconSearch, IconX } from "@tabler/icons-react";

export type SearchResult = {
  displayName: string;
  center: [number, number];
  zoom: number;
};

type NominatimResult = {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  boundingbox: [string, string, string, string];
};

type SearchBarProps = {
  onSelect: (result: SearchResult) => void;
};

const NOMINATIM_URL = "https://nominatim.openstreetmap.org/search";
const DEBOUNCE_MS = 350;

function zoomFromBoundingBox(bbox: [string, string, string, string]): number {
  const latSpan = Math.abs(Number.parseFloat(bbox[1]) - Number.parseFloat(bbox[0]));
  const lonSpan = Math.abs(Number.parseFloat(bbox[3]) - Number.parseFloat(bbox[2]));
  const maxSpan = Math.max(latSpan, lonSpan);
  if (maxSpan < 0.003) return 17;
  if (maxSpan < 0.01) return 16;
  if (maxSpan < 0.05) return 14;
  if (maxSpan < 0.1) return 13;
  if (maxSpan < 0.5) return 11;
  if (maxSpan < 2) return 9;
  if (maxSpan < 10) return 7;
  return 6;
}

export function SearchBar({ onSelect }: SearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isFetching, setIsFetching] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchResults = useCallback(async (q: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsFetching(true);
    try {
      const params = new URLSearchParams({
        q,
        format: "json",
        addressdetails: "1",
        limit: "5",
        "accept-language": "en",
      });
      const response = await fetch(`${NOMINATIM_URL}?${params}`, {
        signal: controller.signal,
        headers: { "User-Agent": "SunnyCoffee/1.0" },
      });
      if (!response.ok) return;
      const data: NominatimResult[] = await response.json();
      if (controller.signal.aborted) return;
      setResults(data);
      setIsOpen(data.length > 0);
      setActiveIndex(-1);
    } catch (error) {
      if (controller.signal.aborted) return;
      setResults([]);
      setIsOpen(false);
    } finally {
      if (!controller.signal.aborted) {
        setIsFetching(false);
      }
    }
  }, []);

  const handleInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (value.trim().length < 2) {
        setResults([]);
        setIsOpen(false);
        abortRef.current?.abort();
        return;
      }

      debounceRef.current = setTimeout(() => {
        fetchResults(value.trim()).catch(() => {});
      }, DEBOUNCE_MS);
    },
    [fetchResults],
  );

  const selectResult = useCallback(
    (result: NominatimResult) => {
      const zoom = zoomFromBoundingBox(result.boundingbox);
      onSelect({
        displayName: result.display_name,
        center: [Number.parseFloat(result.lon), Number.parseFloat(result.lat)],
        zoom,
      });
      setResults([]);
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!isOpen || results.length === 0) return;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex(i => (i < results.length - 1 ? i + 1 : 0));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex(i => (i > 0 ? i - 1 : results.length - 1));
      } else if (event.key === "Enter" && activeIndex >= 0 && results[activeIndex]) {
        event.preventDefault();
        selectResult(results[activeIndex]);
      } else if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [isOpen, results, activeIndex, selectResult],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <div ref={containerRef} className="relative mt-3">
      <div className="relative">
        <IconSearch
          size={16}
          stroke={2}
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
        />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => results.length > 0 && setIsOpen(true)}
          placeholder="Search street, city, or neighborhood…"
          className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-8 text-sm text-slate-900 placeholder:text-slate-400 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
        />
        {(query.length > 0 || isFetching) && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => {
              setQuery("");
              setResults([]);
              setIsOpen(false);
              inputRef.current?.focus();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-slate-400 hover:text-slate-600"
          >
            <IconX size={14} stroke={2} />
          </button>
        )}
      </div>

      {isOpen && results.length > 0 && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {results.map((result, index) => (
            <li
              key={result.place_id}
              role="option"
              aria-selected={index === activeIndex}
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => selectResult(result)}
              className={`cursor-pointer px-3 py-2 text-sm ${
                index === activeIndex
                  ? "bg-amber-50 text-slate-900"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="line-clamp-2">{result.display_name}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
